import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { DatasetLoadingConf, LoadingConfig } from "components/dashboard/charts/interfaces";
import { DweenTimeUnitEnum } from "utils/enums";
import { InputSwitch } from "primereact/inputswitch";
import { DATE_PATTERN_MINUTES, datePaternOptions } from "utils/constants";
import { defaultLoadConf } from "components/dashboard/data-input-output-table/defaultConfigs";
import { AdditionalDateColumnType, DayOfWeekDisplayType } from "models/dataset-models";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import React, { useCallback, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { SortDirection } from "gql/generated/types";
import DatasetDataInputTableDatasetsEditor from "./DatasetDataInputTableDatasetsEditor";
import { DatasetDataInputTableProps } from "../DatasetDataInputTableTypes";

export const defaultDatasetDataInputTableConfig: DatasetDataInputTableProps = {
  tableHeight: 400,
  loadConf: defaultLoadConf as DatasetLoadingConf,
  stepSize: 1,
  stepUnit: DweenTimeUnitEnum.HOURS,
  sort: SortDirection.Asc,
  datasets: [],
};

const additionalDateColumnOptions = [
  { label: T("word.none"), value: AdditionalDateColumnType.NONE },
  { label: T("word.week"), value: AdditionalDateColumnType.WEEK },
  { label: T("word.day-of-week"), value: AdditionalDateColumnType.DAY_OF_WEEK },
];

const dayOfWeekDisplayTypeOptions = [
  { label: T("word.short"), value: DayOfWeekDisplayType.SHORT },
  { label: T("word.long"), value: DayOfWeekDisplayType.LONG },
];

const sortOptions = [
  { label: T("word.ascending"), value: SortDirection.Asc },
  { label: T("word.descending"), value: SortDirection.Desc },
];

const DatasetDataInputTableEditor = ({
  config,
  onConfigChange,
}: DashboardEditorProps<DatasetDataInputTableProps>) => {
  const conf = config && Object.keys(config).length !== 0 ? config : defaultDatasetDataInputTableConfig;

  const selectedDatasets = conf.datasets.map(dataset => dataset.identifier);

  const [currentConfig, setCurrentConfig] = useState<DatasetDataInputTableProps>(conf);
  const [datasetsEditorDialogVisibility, setDatasetsEditorDialogVisibility] = useState(false);

  useEffect(() => {
    if (currentConfig.datasets) {
      onConfigChange(currentConfig);
    }
  }, [currentConfig]);

  const handleLoadConfDetailsChange = useCallback((e: InputNumberValueChangeEvent | DropdownChangeEvent) => {
    const key = e.target.id as keyof LoadingConfig;

    let value: number | string;

    if (typeof e.value === "number") {
      value = e.value;
    } else {
      value = String(e.value).toUpperCase();
    }

    setCurrentConfig(
      prev =>
        ({
          ...prev,
          loadConf: {
            ...prev.loadConf,
            [key]: value,
          },
        }) as DatasetDataInputTableProps
    );
  }, []);

  const handleDatasetChange = useCallback(
    (selectedIdentifiers: string[]): void => {
      const selectedDatasetsArray = selectedIdentifiers.map(identifier => {
        const oldDataset = currentConfig.datasets?.find(d => d.identifier === identifier);
        return oldDataset ?? { identifier };
      });

      setCurrentConfig(prev => ({ ...prev, datasets: selectedDatasetsArray }));
    },
    [currentConfig.datasets]
  );

  const handleEditDatasets = useCallback(() => {
    setDatasetsEditorDialogVisibility(true);
  }, []);

  const handleDatasetDatainputTableDialogFooter = () => {
    return (
      <Button
        label={T("action.close")}
        icon="pi pi-times"
        type="button"
        className="component-editor-button icon-red"
        onClick={() => setDatasetsEditorDialogVisibility(false)}
      />
    );
  };

  return (
    <div className="grid p-fluid flex flex-column">
      <div className="col-12 flex flex-column mb-3">
        <label htmlFor="identifiers">{T("word.identifiers")}</label>
        <div className="flex gap-2">
          <DatasetMultiSelect value={selectedDatasets} onChange={handleDatasetChange} />
          {selectedDatasets.length > 0 && (
            <>
              <Button
                icon="pi pi-pencil"
                type="button"
                className="p-button-secondary"
                tooltip={T("action.edit-lines")}
                onClick={handleEditDatasets}
              />
              <Dialog
                onHide={() => setDatasetsEditorDialogVisibility(false)}
                visible={datasetsEditorDialogVisibility}
                header={T("action.edit-line-style")}
                footer={handleDatasetDatainputTableDialogFooter}
                style={{ width: "min(100vw, 1000px)", height: "400px" }}
              >
                <DatasetDataInputTableDatasetsEditor
                  currentConfig={currentConfig}
                  setCurrentConfig={setCurrentConfig}
                />
              </Dialog>
            </>
          )}
        </div>
      </div>
      <div className="p-12 flex flex-column gap-3">
        <div className="flex justify-content-between gap-3">
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="lag">{T("load-conf.lag")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="lag"
                className="w-6"
                value={currentConfig.loadConf.lag}
                onValueChange={handleLoadConfDetailsChange}
              />
              <TimeUnitDropdown
                id="lagUnit"
                className="p-inputgroup-addon p-0 w-6"
                value={currentConfig.loadConf.lagUnit}
                onChange={handleLoadConfDetailsChange}
              />
            </div>
          </div>
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="lead">{T("load-conf.lead")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="lead"
                className="w-6"
                value={currentConfig.loadConf.lead}
                onValueChange={handleLoadConfDetailsChange}
              />
              <TimeUnitDropdown
                id="leadUnit"
                className="p-inputgroup-addon p-0 w-6"
                value={currentConfig.loadConf.leadUnit}
                onChange={handleLoadConfDetailsChange}
              />
            </div>
          </div>
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="alignUnit">{T("load-conf.align-unit")}</label>
            <TimeUnitDropdown
              id="alignUnit"
              className="w-full"
              value={currentConfig.loadConf.alignUnit || ""}
              onChange={handleLoadConfDetailsChange}
            />
          </div>
        </div>
        <div className="flex flex-row justify-content-start gap-3">
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="stepUnit">{T("load-conf.steps")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="step"
                className="w-6"
                min={1}
                value={currentConfig.stepSize}
                onValueChange={e => {
                  setCurrentConfig(prev => ({ ...prev, stepSize: e.value ?? 1 }));
                }}
              />
              <TimeUnitDropdown
                id="stepUnit"
                className="p-inputgroup-addon p-0 w-6"
                value={currentConfig.stepUnit || ""}
                onChange={e => {
                  setCurrentConfig(prev => ({
                    ...prev,
                    stepUnit: e.value.toString().toUpperCase(),
                  }));
                }}
              />
            </div>
          </div>
          <div className="w-3 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="datePattern">{T("load-conf.date-pattern")}</label>
            <Dropdown
              id="datePattern"
              options={datePaternOptions}
              value={currentConfig.datePattern ?? DATE_PATTERN_MINUTES}
              className="w-full"
              onChange={e => {
                setCurrentConfig(prev => ({
                  ...prev,
                  datePattern: e.value,
                }));
              }}
            />
          </div>
          <div className="w-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="tableHeight">{T("label.table-height")}</label>
            <InputNumber
              id="tableHeight"
              value={currentConfig.tableHeight}
              onChange={e => {
                setCurrentConfig(prev => ({
                  ...prev,
                  tableHeight: e.value ?? 0,
                }));
              }}
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="legacyForcedLtTimezone">{T("action.force-lt-timezone")}</label>
            <InputSwitch
              id="legacyForcedLtTimezone"
              checked={currentConfig.legacyForcedLtTimezone ?? false}
              onChange={e => {
                setCurrentConfig(prev => ({
                  ...prev,
                  legacyForcedLtTimezone: e.value,
                }));
              }}
            />
          </div>
        </div>
        <div className="w-full flex gap-2 flex-wrap md:flex-nowrap ml-2 mb-3">
          <div className="flex flex-column gap-2 w-2">
            <label htmlFor="sort">{T("load-conf.default-date-sorting")}</label>
            <Dropdown
              id="sort"
              value={currentConfig.sort}
              className="w-full"
              options={sortOptions}
              onChange={e => {
                setCurrentConfig(prev => ({
                  ...prev,
                  sort: e.value.toString().toUpperCase(),
                }));
              }}
            />
          </div>
          <div className="flex flex-column gap-2 w-3">
            <label htmlFor="additionalDateColumn">{T("load-conf.additional-date-column")}</label>
            <Dropdown
              id="additionalDateColumn"
              value={currentConfig.additionalDateColumn}
              options={additionalDateColumnOptions}
              onChange={e => {
                setCurrentConfig(prev => ({
                  ...prev,
                  additionalDateColumn: e.value,
                }));
              }}
            />
          </div>
          {currentConfig.additionalDateColumn &&
            currentConfig.additionalDateColumn === AdditionalDateColumnType.DAY_OF_WEEK && (
              <div className="flex flex-column gap-2 w-3">
                <label htmlFor="">{T("load-conf.day-of-week-display-type")}</label>
                <Dropdown
                  id="dayOfWeekDisplayType"
                  value={currentConfig.dayOfWeekDisplayType ?? DayOfWeekDisplayType.SHORT}
                  options={dayOfWeekDisplayTypeOptions}
                  onChange={e => {
                    setCurrentConfig(prev => ({
                      ...prev,
                      dayOfWeekDisplayType: e.value,
                    }));
                  }}
                />
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default DatasetDataInputTableEditor;
